import { useCallback, useEffect } from 'react';
import { cn } from '@/util/cn';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { SubscriberDataEvent, useAnalytics } from '@/tracking/analytics';
import { PlaningViewsEnum } from '@/components/common/PlanYourTrip/constants';
import { useNoHeader } from '@/util/useNoHeader';
import isLiftsUrlWhiteListed from '@/util/isLiftsUrlWhiteListed';
import { isResortWithoutWeSki } from '@/util/isResortWithoutWeSki';
import { getLocale } from '@/i18n/locale';
import ViewTab from './ViewTab';
import { ContextKey, TRKeysByContextType } from './types';
import { classifyRental } from './visibility';

// Underline tab bar: all tabs — active, inactive, or external link —
// sit on a shared hairline baseline. The active tab gets a 2px primary
// underline; inactive tabs and external CTAs are quiet grey text
// (external ones get a trailing ↗ arrow).

const tripControlBlockClass = [
  'mb-4 flex flex-wrap items-center gap-x-1',
  'border-b border-border',
  '[&_a]:no-underline',
].join(' ');

const tabBaseClass = [
  'inline-flex items-center gap-1.5 whitespace-nowrap',
  '-mb-px px-2.5 py-1.5 text-xs font-medium uppercase tracking-wide transition',
  'border-b-2 border-transparent',
  '[&_svg]:h-3 [&_svg]:w-3',
].join(' ');

const viewTabClass = [
  tabBaseClass,
  'bg-transparent text-muted-foreground hover:border-border hover:text-foreground',
].join(' ');

const activeTabClass = [
  tabBaseClass,
  'bg-transparent border-primary text-secondary',
].join(' ');

const weskiTextClass = '!text-[#10ABD9]';

interface ITripControlsBlockProps {
  title: string
  liftsUrl?: string
  rentalUrl?: string
  lessonsUrl?: string
  activeView: PlaningViewsEnum
  onButtonClick: (value: PlaningViewsEnum) => void
  isPartner: boolean
  contextKey: ContextKey
  resortCountry?: string
  hasSkiRental: boolean
  redirectSkiRentalPath?: string
  bookFlightsCb?: () => void
  carRentalsCb?: () => void
  initialTab: PlaningViewsEnum,
  lodgingProvider?: 'weski',
  templateName?: string,
  resortId?: string;
  respectNoHeader?: boolean;
}

const TRKeysByContext: TRKeysByContextType<'common'> = {
  resort: 'tripWidget.resortLodging',
  region: 'tripWidget.regionLodging',
  weski: 'tripWidget.resortHolidays',
};

const TripControlBlock = (
  {
    title,
    liftsUrl,
    activeView,
    rentalUrl: rentalServerUrl,
    redirectSkiRentalPath,
    lessonsUrl,
    onButtonClick,
    isPartner,
    contextKey,
    hasSkiRental,
    resortCountry,
    initialTab = PlaningViewsEnum.lodging,
    lodgingProvider,
    templateName,
    resortId,
    respectNoHeader = true,
  }: ITripControlsBlockProps,
) => {
  const { t } = useOTSTranslation('common');
  const analytic = useAnalytics();
  const { noHeader } = useNoHeader();
  const effectiveNoHeader = respectNoHeader ? noHeader : false;
  const rental = classifyRental({
    hasSkiRental,
    isPartner,
    initialTab,
    redirectSkiRentalPath,
    rentalUrl: rentalServerUrl,
    resortCountry,
  });
  const showLifts = liftsUrl && (isPartner || isLiftsUrlWhiteListed(liftsUrl));
  const withAnalytic = !(effectiveNoHeader || (!liftsUrl && !lessonsUrl && !rental.showTab && !isPartner));
  const useLodgingAsUrl = activeView === PlaningViewsEnum.flights || activeView === PlaningViewsEnum.carRental;

  const locale = getLocale();
  const hasLodgingPage = lodgingProvider === 'weski';
  const shouldHideWeSkiLodging = isResortWithoutWeSki(resortId?.toString() || '', locale);
  const lessonsTabActsAsLink = true;

  const onLodgingClick = useCallback(() => {
    if (!useLodgingAsUrl) {
      onButtonClick(PlaningViewsEnum.lodging);
    }
  }, [useLodgingAsUrl]);

  const onLiftsClick = useCallback(() => {
    analytic.subcriberEvent(SubscriberDataEvent.LiftAccessNavigation, { url: liftsUrl });
  }, [analytic, liftsUrl]);

  const onLessonsClick = useCallback(() => {
    if (!lessonsTabActsAsLink) {
      onButtonClick(PlaningViewsEnum.lessons);
    }

    if (lessonsTabActsAsLink) {
      analytic.subcriberEvent(SubscriberDataEvent.LessonsWidget, { url: lessonsUrl });
    }
  }, [analytic, lessonsTabActsAsLink, lessonsUrl, onButtonClick]);

  const onRentalClick = useCallback(() => {
    if (rental.asTab) {
      onButtonClick(PlaningViewsEnum.rental);
      return;
    }
    if (rental.isExternalUrl && withAnalytic) {
      analytic.subcriberEvent(SubscriberDataEvent.RentalsWidget, { url: rentalServerUrl });
    }
  }, [analytic, rentalServerUrl, rental.isExternalUrl, rental.asTab]);

  const applyActiveStyles = useCallback((key: PlaningViewsEnum) => (
    key === activeView ? activeTabClass : viewTabClass
  ), [activeView, initialTab]);

  const isLodgingMode = initialTab === PlaningViewsEnum.lodging;

  const getActiveLodgingName = () => {
    let newContextKey = contextKey;
    if (lodgingProvider === 'weski') {
      newContextKey = lodgingProvider;
    }

    if (activeView === PlaningViewsEnum.lodging && isLodgingMode) {
      return `${title} ${t(TRKeysByContext[newContextKey], { [newContextKey]: '' })}`;
    }
    return t(TRKeysByContext[newContextKey]);
  };

  const activeLodgingName = getActiveLodgingName();

  const showMoreThanOneTab = !(templateName === 'Region Lodging' || templateName === 'Resort Lodging');

  // Determine which tabs are visible
  const visibleTabs: PlaningViewsEnum[] = [];
  if (!effectiveNoHeader && showMoreThanOneTab && hasLodgingPage && !shouldHideWeSkiLodging) {
    visibleTabs.push(PlaningViewsEnum.lodging);
  }
  if (!effectiveNoHeader && rental.showTab && showMoreThanOneTab && rental.asTab) {
    visibleTabs.push(PlaningViewsEnum.rental);
  }
  if (!effectiveNoHeader && showLifts && showMoreThanOneTab) {
    // There is no PlaningViewsEnum.lifts, so we skip adding it
    // visibleTabs.push(PlaningViewsEnum.lifts);
  }
  if (!effectiveNoHeader && lessonsUrl && showMoreThanOneTab) {
    visibleTabs.push(PlaningViewsEnum.lessons);
  }

  // If activeView is not visible, switch to the first visible tab
  useEffect(() => {
    if (!hasLodgingPage || shouldHideWeSkiLodging) {
      return;
    }

    if (visibleTabs.length > 0 && !visibleTabs.includes(activeView)) {
      onButtonClick(visibleTabs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, rental.showTab, showLifts, lessonsUrl, showMoreThanOneTab, effectiveNoHeader, resortId, hasLodgingPage, shouldHideWeSkiLodging]);
  const showLodgingTab = !effectiveNoHeader && showMoreThanOneTab && hasLodgingPage && !shouldHideWeSkiLodging;
  const showRentalAsTab = !effectiveNoHeader && rental.showTab && showMoreThanOneTab && !rental.asLink;
  const showRentalAsLink = !effectiveNoHeader && rental.showTab && showMoreThanOneTab && rental.asLink;
  const showLiftsLink = !effectiveNoHeader && showLifts && showMoreThanOneTab;
  const showLessonsLink = !effectiveNoHeader && lessonsUrl && isPartner && showMoreThanOneTab && lessonsTabActsAsLink;
  const showLessonsTab = !effectiveNoHeader && lessonsUrl && isPartner && showMoreThanOneTab && !lessonsTabActsAsLink;
  // A single tab with no links alongside is just a label of the
  // current view — no actual choice to make. Skip the tab nav
  // entirely; the panel beneath still renders. A lone tab does show
  // when external links sit next to it, so the row reads as a tab bar.
  const hasAnyLinks = Boolean(showRentalAsLink || showLiftsLink || showLessonsLink);
  const tabCount = [showLodgingTab, showRentalAsTab, showLessonsTab].filter(Boolean).length;
  const showLodging = showLodgingTab && (tabCount > 1 || hasAnyLinks);
  const showTabRow = tabCount > 1 || hasAnyLinks;

  if (!showTabRow) {
    return null;
  }

  return (
    <div className={tripControlBlockClass}>
      {showLodging && (
        <ViewTab
          title={activeLodgingName}
          className={cn(applyActiveStyles(PlaningViewsEnum.lodging), lodgingProvider === 'weski' && weskiTextClass)}
          onClick={onLodgingClick}
        />
      )}
      {showRentalAsTab && (
        <ViewTab
          title={t('tripWidget.skiRental')}
          className={applyActiveStyles(PlaningViewsEnum.rental)}
          onClick={onRentalClick}
        />
      )}
      {showRentalAsLink && (
        <ViewTab
          title={t('tripWidget.skiRental')}
          className={viewTabClass}
          onClick={onRentalClick}
          url={rental.url}
        />
      )}
      {showLiftsLink && (
        <ViewTab
          title={t('tripWidget.liftAccess')}
          className={viewTabClass}
          onClick={onLiftsClick}
          url={liftsUrl}
        />
      )}
      {showLessonsTab && (
        <ViewTab
          title={t('tripWidget.skiLessons')}
          className={applyActiveStyles(PlaningViewsEnum.lessons)}
          onClick={onLessonsClick}
        />
      )}
      {showLessonsLink && (
        <ViewTab
          title={t('tripWidget.skiLessons')}
          className={viewTabClass}
          onClick={onLessonsClick}
          url={lessonsUrl}
          isExternal
        />
      )}
    </div>
  );
};

export default TripControlBlock;
